import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, Loader2, XCircle, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MESSAGES = {
  subscription: { title: 'Abbonamento attivo', body: 'Grazie! Il tuo piano è attivo e trovi le fatture nella pagina Abbonamento.', cta: 'Vai all\'abbonamento', to: '/settings?tab=subscription' },
  gift_card: { title: 'Gift Card pagata', body: 'Il codice è stato inviato per email al destinatario. Si presenta in salone per usarla.', cta: null },
  deposit: { title: 'Acconto ricevuto, appuntamento confermato', body: 'Ti arriva il promemoria prima dell\'appuntamento. Il resto si salda in salone.', cta: null },
};

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking');
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let attempts = 0;
    let stop = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (stop) return;
        setInfo(res.data);
        if (res.data.payment_status === 'paid') { setState('paid'); return; }
        if (['failed', 'expired'].includes(res.data.payment_status)) { setState('failed'); return; }
      } catch {
        if (attempts > 3) { setState('error'); return; }
      }
      attempts += 1;
      if (attempts > 15) { setState('slow'); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  const copy = MESSAGES[info?.purpose] || MESSAGES.subscription;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
      <Card className="w-full max-w-md shadow-lg" data-testid="payment-success-page">
        <CardContent className="p-8 text-center space-y-5">
          {state === 'checking' && (
            <>
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
              <h2 className="text-xl font-semibold">Sto confermando il pagamento…</h2>
              <p className="text-sm text-muted-foreground">Un attimo solo, non chiudere la pagina.</p>
            </>
          )}
          {state === 'paid' && (
            <>
              <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center bg-primary/15">
                <Check className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold" data-testid="payment-paid-title">{copy.title}</h2>
              {info?.amount != null && (
                <p className="text-3xl font-bold" data-testid="payment-amount">€{info.amount.toFixed(2)}</p>
              )}
              <p className="text-sm text-muted-foreground">{copy.body}</p>
              {copy.cta && (
                <Button className="w-full min-h-[44px]" onClick={() => navigate(copy.to)} data-testid="payment-cta-btn">
                  {copy.cta}
                </Button>
              )}
            </>
          )}
          {state === 'slow' && (
            <>
              <AlertCircle className="w-10 h-10 mx-auto text-amber-500" />
              <h2 className="text-xl font-semibold">Il pagamento è in lavorazione</h2>
              <p className="text-sm text-muted-foreground">
                La banca ci sta mettendo qualche secondo in più. Ricarica fra poco: se è andato a buon fine
                vedrai la conferma qui.
              </p>
              <Button variant="outline" className="w-full min-h-[44px]" onClick={() => window.location.reload()}
                data-testid="payment-retry-btn">Ricarica</Button>
            </>
          )}
          {(state === 'failed' || state === 'error') && (
            <>
              <XCircle className="w-10 h-10 mx-auto text-destructive" />
              <h2 className="text-xl font-semibold">Pagamento non riuscito</h2>
              <p className="text-sm text-muted-foreground">Non è stato addebitato nulla. Puoi riprovare quando vuoi.</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
    <Card className="w-full max-w-md shadow-lg" data-testid="payment-cancel-page">
      <CardContent className="p-8 text-center space-y-4">
        <XCircle className="w-12 h-12 mx-auto text-muted-foreground" />
        <h2 className="text-xl font-semibold">Pagamento annullato</h2>
        <p className="text-sm text-muted-foreground">
          Non è stato addebitato nulla. Se era una prenotazione, l'orario resta libero per qualcun altro.
        </p>
        <Button variant="outline" className="w-full min-h-[44px]" onClick={() => window.history.back()}
          data-testid="payment-back-btn">Torna indietro</Button>
      </CardContent>
    </Card>
  </div>
);
